import { ROOT_CONTAINER, findLayoutItem, layoutItemKey, moveLayoutItem } from '@repo/lib';

const kindOf = key => String(key).split(':')[0];

// Nothing moves aside while dragging: the drop line shows where the item lands. Rows that slid
// under a still pointer kept changing the target and made drags past groups jumpy.
export const staticStrategy = () => null;

// `into` marks a group other than the one the drag started in: it is one target, filled from the
// bottom, so a drag passes it without the fields inside moving.
export function applyDropTarget(layout, activeKey, target) {
  const active = findLayoutItem(layout, activeKey);
  if (!active || !target || target.beforeKey === activeKey) return layout;
  const list =
    target.containerId === ROOT_CONTAINER
      ? layout
      : layout.find(item => item.type === 'group' && item.id === target.containerId)?.items;
  if (!list) return layout;
  let index = target.beforeKey
    ? list.findIndex(item => layoutItemKey(item) === target.beforeKey)
    : list.length;
  if (index === -1) return layout;
  if (active.containerId === target.containerId && active.index < index) index -= 1;
  if (active.containerId === target.containerId && active.index === index) return layout;
  return moveLayoutItem(layout, activeKey, { containerId: target.containerId, index });
}

const sameLayout = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);

/** Where a pointer at `y` drops the item; null when the drop would change nothing. */
export function dropTargetAt(layout, activeKey, y, rectOf) {
  const active = findLayoutItem(layout, activeKey);
  if (!active) return null;
  const slotIn = (items, containerId) => {
    const next = items.find(item => {
      const rect = rectOf(layoutItemKey(item));
      return rect && y < (rect.top + rect.bottom) / 2;
    });
    return { containerId, beforeKey: next ? layoutItemKey(next) : null };
  };

  let target = null;
  if (kindOf(activeKey) !== 'group') {
    for (const item of layout) {
      if (item.type !== 'group') continue;
      const rect = rectOf(layoutItemKey(item));
      if (!rect || y < rect.top || y > rect.bottom) continue;
      target =
        item.id === active.containerId
          ? slotIn(item.items, item.id)
          : { containerId: item.id, beforeKey: null, into: true };
      break;
    }
  }
  target ??= slotIn(layout, ROOT_CONTAINER);
  return sameLayout(applyDropTarget(layout, activeKey, target), layout) ? null : target;
}

/** Every distinct target top to bottom, for the arrow keys; `current` leaves the layout as it is. */
export function dropStops(layout, activeKey) {
  const active = findLayoutItem(layout, activeKey);
  if (!active) return { stops: [], current: -1 };
  const stops = [];
  const push = target => {
    const result = JSON.stringify(applyDropTarget(layout, activeKey, target));
    if (stops.at(-1)?.result === result) return;
    stops.push({ target, result });
  };

  for (const item of layout) {
    push({ containerId: ROOT_CONTAINER, beforeKey: layoutItemKey(item) });
    if (item.type !== 'group' || kindOf(activeKey) === 'group') continue;
    if (item.id === active.containerId) {
      for (const child of item.items)
        push({ containerId: item.id, beforeKey: layoutItemKey(child) });
      push({ containerId: item.id, beforeKey: null });
    } else {
      push({ containerId: item.id, beforeKey: null, into: true });
    }
  }
  push({ containerId: ROOT_CONTAINER, beforeKey: null });

  const unchanged = JSON.stringify(layout);
  return {
    stops: stops.map(stop => stop.target),
    current: stops.findIndex(stop => stop.result === unchanged),
  };
}
